/**
 * Desktop sidebar (PHASE1 §7.1) — visible ≥md. Full nav, current user, logout.
 * Visuals per DESIGN.md §3.2/§3.3: slate-50 surface, sky logo tile, nav items
 * with a sky left bar when active.
 */
import { NavLink } from 'react-router';
import { LogOut, Paperclip, PiggyBank } from 'lucide-react';
import { navItemsFor, type NavItem } from './AppShell.tsx';
import { useCurrentUser, useLogout } from '../../lib/hooks/useCurrentUser.ts';
import { useTheme } from '../../context/ThemeContext.tsx';

// "Belege" isn't part of the canonical `AppShell.NAV_ITEMS` list (it stays out
// of the mobile bottom bar's primary/overflow split); it's spliced in here,
// next to Transaktionen, and separately in `MoreSheet.tsx` for mobile.
const BELEGE_NAV_ITEM: NavItem = { to: '/belege', label: 'Belege', icon: Paperclip };

function withBelege(items: NavItem[]): NavItem[] {
  const index = items.findIndex((item) => item.to === '/transaktionen');
  if (index === -1) return [...items, BELEGE_NAV_ITEM];
  return [...items.slice(0, index + 1), BELEGE_NAV_ITEM, ...items.slice(index + 1)];
}

/** Inline sun/moon glyphs for the theme toggle (no icon-CDN dependency). */
function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5 shrink-0"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5 shrink-0"
      aria-hidden
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
    </svg>
  );
}

export function Sidebar() {
  const user = useCurrentUser();
  const { logout, isLoggingOut } = useLogout();
  const { theme, toggleTheme } = useTheme();
  const themeLabel = theme === 'dark' ? 'Zum hellen Modus wechseln' : 'Zum dunklen Modus wechseln';

  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-slate-200 bg-slate-50 md:flex dark:border-slate-800 dark:bg-slate-900">
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-200/60 px-5 dark:border-slate-800/70">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-600 dark:bg-sky-500">
          <PiggyBank className="h-5 w-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 leading-tight">
          <p className="truncate text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Finance Tracker
          </p>
          <p className="truncate text-[11px] text-slate-400 dark:text-slate-500">Haushaltsbudget</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {withBelege(navItemsFor(user)).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? 'bg-sky-50 text-sky-700 before:absolute before:left-0 before:top-1/2 before:h-[60%] before:w-[3px] before:-translate-y-1/2 before:rounded-r-[2px] before:bg-sky-600 dark:bg-sky-950 dark:text-sky-300 dark:before:bg-sky-500'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100'
              }`
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-200/60 px-4 py-3 dark:border-slate-800/70">
        <p className="truncate px-3 text-xs text-slate-500 dark:text-slate-400" title={user.email}>
          {user.display_name ?? user.email}
        </p>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={themeLabel}
          title={themeLabel}
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100 dark:focus-visible:outline-sky-500"
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          {theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
        </button>
        <button
          type="button"
          onClick={() => void logout()}
          disabled={isLoggingOut}
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:opacity-60 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100 dark:focus-visible:outline-sky-500"
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {isLoggingOut ? 'Wird abgemeldet …' : 'Abmelden'}
        </button>
      </div>
    </aside>
  );
}
